import { useCallback, useEffect } from 'react';
import { dispatchDataEvent, DataEvents } from '@/db/events';
import { isOnline } from '@/lib/connectivity';
import { isTransientSyncError } from '@/lib/sync-errors';
import { createLogger, LogLevel } from '@/lib/logger';

const logger = createLogger('ClientDocumentsRefresh', LogLevel.INFO);

/**
 * Keeps a client's document list fresh while the Documents tab is showing.
 *
 * Refreshes once on mount (and whenever the client id or tab changes) and
 * returns `refresh` for pull-to-refresh. The mount-time refresh is
 * best-effort: on a device that can go offline, "the server is not reachable
 * right now" is an expected outcome, not an error, so connectivity failures
 * (offline guard, NetworkError, TimeoutError) are swallowed and anything else
 * is logged rather than escaping as an unhandled rejection. The explicit
 * `refresh` rejects normally so the caller can show feedback.
 *
 * Gates on the connectivity service rather than `navigator.onLine`: the data
 * layer refuses to refresh while the server is unreachable, and asking it
 * with a bare `onLine` check is what produced the "Cannot refresh documents
 * while offline" rejections.
 */
export function useClientDocumentsRefresh(
  clientId: number | null | undefined,
  activeTab: 'profile' | 'documents',
): { refresh: () => Promise<void> } {
  const refresh = useCallback(async () => {
    if (!isOnline()) return;
    // The profile tab reads live client data and has nothing to refresh.
    if (activeTab !== 'documents' || !clientId) return;
    await dispatchDataEvent(DataEvents.REFRESH_CLIENT_DOCUMENTS, { clientId });
  }, [activeTab, clientId]);

  // clientId resolves asynchronously from the route slug, so this also fires
  // the mount-time refresh once the id lands instead of skipping it.
  useEffect(() => {
    refresh().catch((error) => {
      if (isTransientSyncError(error)) return;
      logger.error('Failed to refresh client documents on mount:', error);
    });
  }, [refresh]);

  return { refresh };
}
